import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  CheckCircle2, 
  Lock, 
  Play, 
  Upload, 
  Workflow, 
  FileText, 
  PenTool,
  ChevronDown,
  ChevronUp,
  Clock
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

export interface JourneyStep {
  id: string;
  step_number: number;
  title: string;
  description: string | null;
  action_type: string;
  action_config: Record<string, any>;
  estimated_minutes: number | null;
}

export interface UserStepProgress {
  id?: string;
  status: 'locked' | 'active' | 'completed';
  started_at?: string;
  completed_at?: string;
  action_data?: Record<string, any>;
}

interface JourneyStepCardProps {
  step: JourneyStep;
  progress: UserStepProgress;
  isActive: boolean;
  defaultCollapsed?: boolean;
  hideDescriptionWhenCompleted?: boolean;
  children?: React.ReactNode;
}

const actionIcons: Record<string, any> = {
  video: Play,
  document_upload: Upload,
  workflow: Workflow,
  form_submission: FileText,
  signature: PenTool,
  acknowledgment: FileText,
  form_data_collection: FileText,
  external_registration: FileText,
  ontraport_check: Clock,
  code_of_conduct_signing: PenTool,
  mini_workflow_with_signature: PenTool,
  reading_acknowledgment: FileText,
  resolution_creation: FileText,
};


export function JourneyStepCard({ step, progress, isActive, defaultCollapsed, hideDescriptionWhenCompleted, children }: JourneyStepCardProps) {
  const [isOpen, setIsOpen] = useState(defaultCollapsed ? false : isActive);
  const Icon = actionIcons[step.action_type] || FileText;
  const isCompleted = progress.status === 'completed';
  const isLocked = progress.status === 'locked';

  const prevRef = useRef({ isActive, isLocked });

  // Auto-expand when this step becomes active/unlocked (e.g. after the prior step completes)
  useEffect(() => {
    const { isActive: wasActive, isLocked: wasLocked } = prevRef.current;
    const becameActive = !wasActive && isActive;
    const becameUnlocked = wasLocked && !isLocked;

    if ((becameActive || becameUnlocked) && isActive && !isCompleted && !isLocked) {
      setIsOpen(true);
    }

    prevRef.current = { isActive, isLocked };
  }, [isActive, isLocked, isCompleted]);

  // Auto-collapse when step becomes completed
  useEffect(() => {
    if (isCompleted) {
      setIsOpen(false);
    }
  }, [isCompleted]);

  return (
    <Card className={cn(
      "transition-all duration-200",
      isActive && !isCompleted && "ring-2 ring-gold shadow-lg border-gold/30",
      isCompleted && "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800",
      isLocked && "opacity-60"
    )}>
      <Collapsible open={isOpen && !isLocked} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors p-3 sm:p-6">
            <div className="flex items-start sm:items-center justify-between gap-2">
              <div className="flex items-start sm:items-center gap-2 sm:gap-4 flex-1 min-w-0">
                {/* Step Number / Status */}
                <div className={cn(
                  "w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0",
                  isCompleted && "bg-green-100 text-green-700",
                  isActive && "bg-gold text-charcoal",
                  isLocked && "bg-muted text-muted-foreground"
                )}>
                  {isCompleted ? (
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                  ) : isLocked ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Lock className="w-3 h-3 sm:w-4 sm:h-4" />
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>Complete previous steps to unlock</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    step.step_number
                  )}
                </div>

                {/* Title and Description */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm sm:text-base">{step.title}</h3>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                {/* Estimated Time - Hidden on mobile */}
                {step.estimated_minutes && !isCompleted && (
                  <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground">
                    <Clock className="w-3 h-3" />
                    {step.estimated_minutes} min
                  </div>
                )}

                {/* Status Badge - Compact on mobile */}
                {isCompleted && progress.completed_at && (
                  <Badge variant="secondary" className="text-xs whitespace-nowrap">
                    <span className="hidden sm:inline">Completed {new Date(progress.completed_at).toLocaleDateString()}</span>
                    <CheckCircle2 className="w-3 h-3 sm:hidden" />
                  </Badge>
                )}

                {/* Expand/Collapse */}
                {!isLocked && (
                  isOpen ? (
                    <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                  )
                )}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="pt-0">
            {isLocked ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Lock className="w-5 h-5 mr-2" />
                Complete the previous step to unlock
              </div>
            ) : (
              <div className="space-y-4">
                {isCompleted && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 px-3 py-2 rounded-md">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span>This step has been completed. View only.</span>
                  </div>
                )}
                {step.description && !(hideDescriptionWhenCompleted && isCompleted) && (
                  <p className="text-muted-foreground whitespace-pre-wrap">{step.description}</p>
                )}
                {children}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
